import React, { useState } from 'react';
import { Upload, X, Loader2, Check } from 'lucide-react';
import { getSessionToken } from '../services/authApi';
import { useAuth } from '../contexts/AuthContext';
import { LS_KEYS } from '../constants';
import { lsGet } from '../utils/storage';

import { API_BASE } from '../config';

// Maps localStorage keys to KV keys
const MIGRATION_MAP = {
  [LS_KEYS.PROFILE]: 'profile',
  [LS_KEYS.PORTFOLIO]: 'portfolio_positions',
  [LS_KEYS.FUNDS]: 'portfolio_funds',
  [LS_KEYS.CHAT]: 'analyst_chat',
  [LS_KEYS.INTELLIGENCE]: 'intelligence_report',
  [LS_KEYS.INTELLIGENCE_FREQ]: 'intelligence_freq',
  [LS_KEYS.INTELLIGENCE_CHAT]: 'intelligence_chat',
  [LS_KEYS.RECOVERY_DATA]: 'recovery_data',
  [LS_KEYS.RISK_CACHE]: 'risk_config',
  [LS_KEYS.MC_CACHE]: 'monte_carlo',
  [LS_KEYS.WATCHLIST]: 'watchlist',
};

const MigrationPrompt = ({ onDismiss }) => {
  const { user } = useAuth();
  const [migrating, setMigrating] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState('');

  // Check if there's any data worth migrating
  const hasData = Object.keys(MIGRATION_MAP).some(lsKey => {
    const val = lsGet(lsKey);
    return val !== null && val !== undefined;
  });

  if (!hasData) return null;

  const handleMigrate = async (scope) => {
    setMigrating(true);
    setError('');
    const token = getSessionToken();
    if (!token) { setError('Non autenticato'); setMigrating(false); return; }

    const data = {};
    for (const [lsKey, kvKey] of Object.entries(MIGRATION_MAP)) {
      const val = lsGet(lsKey);
      if (val !== null && val !== undefined) data[kvKey] = val;
    }

    try {
      const res = await fetch(`${API_BASE}/api/data/migrate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}`, 'X-Requested-With': 'XMLHttpRequest' },
        body: JSON.stringify({ scope, data }),
      });
      const result = await res.json();
      if (!res.ok) throw new Error(result.error || 'Errore migrazione');

      // Clear migrated localStorage keys so the banner won't reappear
      for (const lsKey of Object.keys(MIGRATION_MAP)) {
        localStorage.removeItem(lsKey);
      }

      setDone(true);
      setTimeout(onDismiss, 2000);
    } catch (err) {
      setError(err.message);
    } finally {
      setMigrating(false);
    }
  };

  if (done) {
    return (
      <div className="bg-green-500/10 border border-green-500/30 rounded-xl p-4 mb-6 flex items-center gap-3">
        <Check className="text-green-500" size={20} />
        <span className="text-green-500 font-medium">Dati migrati con successo!</span>
      </div>
    );
  }

  return (
    <div className="bg-blue-500/10 border border-blue-500/30 rounded-xl p-4 mb-6">
      <div className="flex items-start justify-between">
        <div className="flex items-center gap-3">
          <Upload className="text-blue-500 flex-shrink-0" size={20} />
          <div>
            <p className="text-txt-primary font-medium text-sm">Sincronizza i tuoi dati</p>
            <p className="text-txt-secondary text-xs mt-1">
              Hai dati salvati solo su questo browser (watchlist, portafoglio, profilo).
              Trasferiscili sul server per accedervi da qualsiasi dispositivo.
            </p>
          </div>
        </div>
        <button onClick={onDismiss} className="text-txt-secondary hover:text-txt-primary p-1" title="Ignora"><X size={16} /></button>
      </div>

      {error && <p className="text-red-500 text-xs mt-2">{error}</p>}

      <div className="flex gap-2 mt-3">
        <button
          onClick={() => handleMigrate('personal')}
          disabled={migrating}
          className="flex-1 py-2 px-3 rounded-lg bg-blue-500 text-white text-sm font-medium hover:bg-blue-600 disabled:opacity-50 flex items-center justify-center gap-1"
        >
          {migrating ? <Loader2 size={14} className="animate-spin" /> : null}
          Trasferisci sul server
        </button>
        {user?.role === 'admin' && (
        <button
          onClick={() => handleMigrate('shared')}
          disabled={migrating}
          className="flex-1 py-2 px-3 rounded-lg bg-purple-500/80 text-white text-sm font-medium hover:bg-purple-600 disabled:opacity-50 flex items-center justify-center gap-1"
          title="Salva come dati condivisi (visibili a tutti gli utenti)"
        >
          {migrating ? <Loader2 size={14} className="animate-spin" /> : null}
          Salva come condivisi
        </button>
        )}
      </div>
    </div>
  );
};

export default MigrationPrompt;
